import { ApolloQueryResult, gql } from "@apollo/client";
import { UninitiatedContentBlock } from "components/admin/custom-blocks/types";
import { RenderContent } from "components/content-renderer";
import { Layout } from "components/layout";
import {
  GetBooksellersAndPostsDocument,
  GetPageContentDocument,
  GetPageContentQueryResult,
  GetPageContentQueryVariables,
  PostCardDataFragmentDoc,
  useGetBooksellersAndPostsQuery,
} from "lib/graphql-generated";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import { GetServerSideProps } from "next";
import Link from "next/link";

const BOOKSELLERS = gql`
  query GetBooksellersAndPosts {
    booksellers(order_by: { name: desc }) {
      id
      name
      avatar
      video_url
      about
      posts(limit: 4, order_by: { created_at: desc }) {
        ...PostCardData
      }
    }
  }
  ${PostCardDataFragmentDoc}
`;

interface Props {
  content: UninitiatedContentBlock<"key-value">;
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();

  const { data }: ApolloQueryResult<GetPageContentQueryResult["data"]> =
    await client.query({
      query: GetPageContentDocument,
      variables: {
        title: "about",
      } as GetPageContentQueryVariables,
    });

  await client.query({
    query: GetBooksellersAndPostsDocument,
  });
  return addApolloState(client, {
    props: {
      content: data.pages_by_pk.content.data[0],
    } as Props,
  });
};

export const AboutBookshop = ({ content }: Props) => {
  const { data } = useGetBooksellersAndPostsQuery();

  return (
    <>
      <Layout pageName='About'>
        <article className='space-y-12 flex flex-col'>
          <header className='bg-green-light w-full '>
            <section className=' max-w-screen-2xl mx-auto flex flex-row max-h-96 px-12 py-6 justify-center space-x-4 md:space-x-6 xl:space-x-8 '>
              <img
                src='/assets/images/value-icon-1.png'
                className=' w-1/4 h-1/2 max-w-2xs max-h-64'
              />
              <img
                src='/assets/images/value-icon-2.png'
                className=' w-1/4 h-1/2 max-w-2xs max-h-64'
              />
              <img
                src='/assets/images/value-icon-3.png'
                className=' w-1/4 h-1/2 max-w-2xs max-h-64'
              />
            </section>
          </header>
          {content.data.map((block, i) => (
            <section
              key={i}
              className='px-12 space-y-2 lg:space-y-0 lg:space-x-20 mx-auto flex flex-col lg:flex-row lg:w-full max-w-screen-lg '>
              <h2 className='uppercase font-header tracking-wide text-3xl  lg:w-1/5 mr-auto lg:pt-2.5'>
                {block.key}
              </h2>
              <div className='prose lg:w-4/5 lg:ml-auto'>
                <RenderContent content={block.value} />
              </div>
            </section>
          ))}

          <section className='w-full max-w-screen-2xl mx-auto bg-green-light p-4 lg:p-16 space-y-2 md:space-y-8  flex flex-col'>
            {data?.booksellers.map((bookseller) => (
              <article
                key={bookseller.id}
                className='w-full flex flex-row flex-wrap md:grid grid-cols-2 gap-x-16 gap-y-2 justify-between p-4 space-y-4 md:space-y-0 bg-yellow-light rounded max-w-screen-lg mx-auto'>
                <div className='w-full'>
                  <div
                    className='video aspect-w-16 aspect-h-9'
                    dangerouslySetInnerHTML={{ __html: bookseller.video_url }}
                  />
                </div>

                <section className=' w-full flex flex-row'>
                  <div className='w-2/3'>
                    <h3 className='header text-4xl'>{bookseller.name}</h3>
                    <h4 className='text-lg underline mt-4 mb-2'>
                      Latest Posts
                    </h4>
                    <ul className='flex-col space-y-1'>
                      {bookseller.posts.map((post) => (
                        <li key={post.slug}>
                          <Link href={`/posts/${post.slug}`}>
                            <a className='group flex text-gray-700   px-1 -ml-1 relative'>
                              <svg className='h-4 w-4 top-1.5 absolute transform rotate-12 '>
                                <use className='' href={`#${post.type}`} />
                              </svg>
                              <span className='group-hover:bg-red-default group-hover:text-white px-1 rounded ml-5'>
                                {post.title}
                              </span>
                            </a>
                          </Link>
                        </li>
                      ))}
                      <li>
                        <Link
                          href={`posts?p=0&bookseller=${encodeURI(
                            JSON.stringify({
                              id: bookseller.id,
                              name: bookseller.name,
                            })
                          )}&types=review&types=list&types=article`}>
                          <a className='hover:bg-red-default text-gray-700 hover:text-white px-2 rounded'>
                            ... View all posts
                          </a>
                        </Link>
                      </li>
                    </ul>
                  </div>
                  <div className='w-1/3 px-4 pb-4'>
                    <div className='rounded-full overflow-hidden aspect-h-1 aspect-w-1'>
                      <img className='' src={bookseller.avatar} />
                    </div>
                  </div>
                </section>
                <p className='col-span-full text-gray-700'>
                  {bookseller.about}
                </p>
              </article>
            ))}
          </section>
        </article>
      </Layout>
    </>
  );
};

export default AboutBookshop;
